import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LoginResponse } from '@chronos/shared';
import { apiFetch } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { ErrorNote, SubmitButton } from '@/components/ui';

/** Shown on first login, and reachable any time from the account menu. */
export function ChangePasswordPage() {
  const { user, applySession, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  const forced = user?.mustChangePassword ?? false;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirm) {
      setError(new Error('The two new passwords do not match'));
      return;
    }
    setError(null);
    setPending(true);
    try {
      const res = await apiFetch<LoginResponse>('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      applySession(res);
      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-slate-50 p-4">
      <form onSubmit={onSubmit} className="card w-full max-w-sm space-y-4 p-6">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">{forced ? 'Choose a new password' : 'Change password'}</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {forced
              ? 'Your account uses a temporary password. Set your own before continuing.'
              : 'At least 10 characters, with an uppercase letter, a lowercase letter and a digit.'}
          </p>
        </div>

        <ErrorNote error={error} />

        <div>
          <label className="label" htmlFor="current">Current password</label>
          <input id="current" type="password" className="input" autoComplete="current-password" required value={currentPassword} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="next">New password</label>
          <input id="next" type="password" className="input" autoComplete="new-password" required minLength={10} value={newPassword} onChange={(e) => setNext(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="confirm">Confirm new password</label>
          <input id="confirm" type="password" className="input" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>

        <SubmitButton pending={pending} className="btn-primary w-full">Update password</SubmitButton>
      </form>
    </div>
  );
}
